// C · Chat (QC1_Chat). Every mode comes from the real state: first (day 0), ask (morning, no plan), proposed (plan card
// waiting), planned / changed (talk changes shown as cards with Undo), evening (wrap + Good night), n2 (back after days
// away: "Your streak is safe"), n3 (chat only: the one "two minutes" offer). Messages = state.messages + the stream
// in appStore.chat (user echo, streamed text, finished messages); the final text message replaces the streamed bubble.
// Phone v2 (F_Chat 'immersive', decided 11-03): her scene fills the screen and the messages sit in the lower part,
// fading out upward, as glass bubbles; tap her (the space above the messages) to hide the chat. The plan card is a
// checklist once accepted (tick = the light celebration, cards.tsx), its rows editable before Looks good (F_Edit);
// the next-card strip above the input; Stuck is talked through here (chatCards.tsx); 'done' notes after a tick.
import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { AppState, Card, ChangeData, ChatErrorCode, Message, PlanData, ProposedCard, RouteStop, StuckChatData, SummaryData } from '@shared/types';
import { C } from '@shared/content/characters';
import { CRISIS, LINES } from '@shared/content/lines';
import { addDays, dateLabel, isBigReward, localMinutes, taskShort } from '@shared/rules';
import {
  useApp, appStore, sendChat, sendPhoto, clearChatFlags, confirmPlan, undoChange, acceptNudge, answerOffer, goodnight, toast,
  updateSettings, errorText, editPlan, editCard, rewardSeen, undoneCard, dismissToast,
} from '../../store';
import { go, setParams, useParams } from '../../router';
import {
  Screen, EmbeddedScreen, XScene, XFace, StreakPill, Bubble, TypingBubble, VoiceBubble, MessageCard, MessageRow, HoldToTalk,
  DeskTalkBar, IconButton, Icon, ScrollArea, BottomSheet, XTime, PrimaryButton, setToastQuiet,
} from '../../components';
import type { ImagePayload } from '../../api/types';
import type { Clip } from '../../audio/recorder';
import { micError } from '../onboarding/Questions';
import { EVENING, word } from './copy';
import { dateHead, dateHeadShort, dayOf, serverNow, timeOf, work } from './util';
import { CardSheet, EditSheet, LaterSheet, TickButton, TodaySheet, stuckOn, tick, type CardValues } from './cards';
import { DoneNote, StuckMsg } from './chatCards';
import s from './ChatScreen.module.css';

export type Mode = 'first' | 'ask' | 'proposed' | 'planned' | 'evening' | 'n2' | 'n3';
/** What the user just sent, shown until the server's copy arrives: typed text, or the photo itself (its thumbnail, as
 *  it will look once it lands; the paper placeholder only when there is no thumbnail). A voice message is a VoiceOut
 *  instead. (Voice used to share the photo's empty echo, so a blank photo frame sat in the chat while the voice was
 *  being transcribed: feedback 11-03.) */
export type Outgoing = { kind: 'text'; text: string } | { kind: 'photo'; thumb: string | null };
/** A voice message on its way (feedback 11-03, approved on the canvas: fb1103 row 8): a voice bubble (mic, the clip's
 *  loudness, its length, "Turning it into text…") until the transcript lands as the user's message, shown with a small
 *  mic. No buddy dots before that. `failed`: it didn't go through; the clip is kept, so Try again sends it as it was. */
export type VoiceOut = { clip: Clip; failed: boolean };
export type Item =
  | { k: 'div'; key: string; text: string }
  | { k: 'msg'; key: string; m: Message; face: boolean }
  | { k: 'stream'; key: string; text: string; face: boolean }
  | { k: 'typing'; key: string; face: boolean }
  | { k: 'echo'; key: string; out: Outgoing }
  | { k: 'voice'; key: string; v: VoiceOut }
  | { k: 'heard'; key: string; text: string; face: boolean }
  | { k: 'first'; key: string; title: string }
  | { k: 'note'; key: string; head: string; text: string }
  | { k: 'eve'; key: string; line: string; data: SummaryData; head: string };

const MIN = 60000;
/** Unsent text handed back by the free-limit message; survives the trip to M4 and back (C remounts). */
let carry: string | null = null;
const isToday = (m: Message, st: AppState) => dayOf(m.ts, st.user.tz) === st.today;
const lastOf = (ms: Message[], f: (m: Message) => boolean) => { for (let i = ms.length - 1; i >= 0; i--) if (f(ms[i])) return ms[i]; return null; };

/** The evening wrap the app builds itself ("Wrap up the day" before the server's evening message). */
function localSummary(st: AppState): { line: string; data: SummaryData } {
  const cards = st.plan.cards.filter((c) => c.st !== 'removed');
  const done = cards.filter((c) => c.st === 'done').length;
  const moved = cards.filter((c) => c.st === 'moved').length;
  const E = EVENING[st.ch];
  const sum = `${word(done)} done${moved ? `, ${word(moved).toLowerCase()} moved to tomorrow` : ''}`;
  return {
    line: done ? E.a + sum + E.b : E.zero,
    data: { done, moved, total: cards.length, perfect: st.plan.perfectGiven, cards: cards.map((c) => ({ title: c.title, st: c.st })) },
  };
}

/** Downscale a photo to a JPEG (<= 1600 px) plus a small thumbnail for the history (server/README "Images"). */
export async function readPhoto(file: File): Promise<ImagePayload> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => rej(new Error('bad image')); i.src = url; });
    const draw = (max: number, q: number) => {
      const k = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
      const cv = document.createElement('canvas');
      cv.width = Math.max(1, Math.round(img.naturalWidth * k)); cv.height = Math.max(1, Math.round(img.naturalHeight * k));
      cv.getContext('2d')!.drawImage(img, 0, 0, cv.width, cv.height);
      return cv.toDataURL('image/jpeg', q);
    };
    const full = draw(1600, 0.82);
    return { b64: full.split(',')[1] || '', mime: 'image/jpeg', thumb: draw(360, 0.7) };
  } finally { URL.revokeObjectURL(url); }
}

export function ChatScreen() {
  const st = useApp((x) => x.state);
  const chat = useApp((x) => x.chat);
  const toastOn = useApp((x) => !!x.toast);
  const params = useParams();
  /** Inside the desktop home (K1): a text field and a mic instead of the phone's hold-to-talk bar (feedback 11-03). */
  const desk = useContext(EmbeddedScreen);
  const [typing, setTyping] = useState(() => carry != null);
  const [draft, setDraft] = useState(() => { const t = carry || ''; carry = null; return t; });
  const [outgoing, setOutgoing] = useState<Outgoing | null>(null);
  const [voice, setVoice] = useState<VoiceOut | null>(null);
  /** "I didn't catch any words" after a voice message with nothing in it: the buddy says it in chat (not saved). */
  const [heard, setHeard] = useState<string | null>(null);
  const alive = useRef(true);
  const [sheet, setSheet] = useState(false);
  const [shortDay, setShortDay] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  // Phone v2: the chat hidden to see her; the card / later / today sheets; F_Edit for a card or a plan row; her help
  // on its way after a stuck reason (she "types" meanwhile).
  const [hidden, setHidden] = useState(false);
  const [cardOpen, setCardOpen] = useState<string | null>(null);
  const [laterOf, setLaterOf] = useState<Card | null>(null);
  const [todayOpen, setTodayOpen] = useState(false);
  const [editing, setEditing] = useState<{ card: string } | { plan: string; i: number } | null>(null);
  const [stuckWait, setStuckWait] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const photoIn = useRef<HTMLInputElement>(null);
  const cameraIn = useRef<HTMLInputElement>(null);

  const openTyping = () => { setHidden(false); setTyping(true); setTimeout(() => input.current?.focus(), 30); };

  // Desktop hotkey / tray "Talk to {n}" (app.tsx) opens the keyboard.
  useEffect(() => { const on = () => openTyping(); window.addEventListener('xm:talk', on); return () => window.removeEventListener('xm:talk', on); }, []);

  // Free limit -> M4 with the unsent text back in the input. Crisis -> M6. Stream errors -> toast.
  useEffect(() => {
    if (chat.limit) {
      const isLim = (m: Message) => m.kind === 'limit';
      const lim = lastOf(chat.pending, isLim) || (!chat.sending ? lastOf(st?.messages || [], isLim) : null);
      if (!lim && chat.sending) return; // the limit message (with the unsent text) is the next event
      const text = lim?.data?.text;
      if (typeof text === 'string' && text) { carry = text; setDraft(text); setTyping(true); }
      clearChatFlags();
      go('M4');
    } else if (chat.crisis) {
      clearChatFlags();
      go('M6');
    } else if (chat.rewardFor && !chat.sending) {
      // A card was completed from chat (complete_card): the same celebration as a tick (phone v2): a big reward opens
      // R (back to the chat after), otherwise the toast with Undo.
      const cur = chat.rewardFor;
      clearChatFlags();
      const now = appStore.get().state;
      const rw = now?.reward;
      if (rw && rw.id === cur && !isBigReward(rw)) {
        const title = now?.plan.cards.find((x) => x.id === cur)?.title || rw.title;
        void rewardSeen();
        toast(`${taskShort(title)}: done · +${rw.gained} points`, { undo: () => { dismissToast(); void undoneCard(cur); } });
      } else go('R', { cur, rbeat: null, from: 'C' });
    } else if (chat.error && !chat.sending) {
      toast(chat.error);
      clearChatFlags();
    }
  }, [chat.limit, chat.crisis, chat.rewardFor, chat.error, chat.sending, chat.pending]);

  useEffect(() => { if (!chat.sending) setOutgoing(null); }, [chat.sending]);
  useEffect(() => () => { alive.current = false; }, []);
  useEffect(() => {
    if (!sheet) return undefined;
    const on = (e: KeyboardEvent) => { if (e.key === 'Escape') setSheet(false); };
    document.addEventListener('keydown', on);
    return () => document.removeEventListener('keydown', on);
  }, [sheet]);

  // A row ticked in the checklist while the log is scrolled up stays where it was (stamp in place): the done note and
  // her line arrive below it, so the log scrolls by what moved (also without scroll anchoring, as on iOS). Scrolled up,
  // the toast makes no room (it covers older messages, not the newest); at the bottom it does, as in F_Chat.
  const msgsRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  const pin = useRef<{ id: string; top: number; until: number } | null>(null);
  const pinRow = (id: string) => {
    const log = msgsRef.current?.parentElement;
    const row = log?.querySelector<HTMLElement>(`[data-card-row="${id}"]`);
    pin.current = log && row && Math.abs(log.scrollTop) > 2 ? { id, top: row.getBoundingClientRect().top, until: Date.now() + 2500 } : null;
  };
  useLayoutEffect(() => {
    const p = pin.current;
    if (!p) return;
    if (Date.now() > p.until) { pin.current = null; return; }
    const log = msgsRef.current?.parentElement;
    const row = log?.querySelector<HTMLElement>(`[data-card-row="${p.id}"]`);
    if (!log || !row) return;
    const d = row.getBoundingClientRect().top - p.top;
    if (Math.abs(d) >= 1) log.scrollTop += d;
  });
  useEffect(() => {
    const log = msgsRef.current?.parentElement;
    if (!log) return undefined;
    const off = () => { pin.current = null; };
    const onScroll = () => setAtBottom(Math.abs(log.scrollTop) <= 2);
    onScroll();
    log.addEventListener('scroll', onScroll, { passive: true });
    log.addEventListener('wheel', off, { passive: true });
    log.addEventListener('touchstart', off, { passive: true });
    log.addEventListener('keydown', off);
    return () => {
      log.removeEventListener('scroll', onScroll); log.removeEventListener('wheel', off);
      log.removeEventListener('touchstart', off); log.removeEventListener('keydown', off);
    };
  }, [hidden, !!st]);
  // Hidden to see her: no toast on top of her (F_Chat); it shows again with the chat if it is still due.
  useEffect(() => { setToastQuiet(hidden); }, [hidden]);
  useEffect(() => () => setToastQuiet(false), []);
  // Stuck talked through here: bring the chat back when she asks (it may have been hidden to see her).
  const lastMsg = st && st.messages.length ? st.messages[st.messages.length - 1] : null;
  useEffect(() => { if (lastMsg?.kind === 'stuck') setHidden(false); }, [lastMsg?.id]);

  const derived = useMemo(() => (st ? derive(st, chat, outgoing, voice, heard, params.chat === 'evening', shortDay, stuckWait) : null), [st, chat, outgoing, voice, heard, params.chat, shortDay, stuckWait]);
  if (!st || !derived) return null;
  const ch = st.ch;
  const c = C[ch];
  const { items, mode, offer, saidGoodnight } = derived;
  const todoLeft = st.plan.cards.filter((x) => x.st === 'todo').length;
  const planTime = st.tomorrowPlanTime || st.user.planTime;

  // ---------------------------------------------------------------- actions
  const send = async (text: string) => {
    const t = text.trim();
    if (!t || chat.sending) return;
    setDraft(''); setTyping(false); setHidden(false); setOutgoing({ kind: 'text', text: t }); setVoice(null); setHeard(null);
    await sendChat({ text: t });
  };
  /** Voice: the bubble shows at once; the bar is back (dimmed until the reply is in). Errors before the transcript
   *  lands are shown on the bubble, not as a toast. */
  const sendVoice = async (clip: Clip) => {
    setOutgoing(null); setHeard(null); setHidden(false); setVoice({ clip, failed: false });
    const got: { landed?: boolean; err?: { code?: ChatErrorCode; message: string } } = {};
    const end = await sendChat({ audio: { b64: clip.b64, mime: clip.mime } }, (e) => {
      if (e.t === 'user' || e.t === 'limit' || e.t === 'crisis') got.landed = true;
      else if (e.t === 'error' && !got.landed) got.err = { code: e.code, message: e.message };
    }, { quiet: true });
    if (got.landed || end === 'aborted') { setVoice(null); return; }
    if (got.err?.code === 'no_words') { setVoice(null); setHeard(got.err.message); return; }
    if (got.err?.code === 'region') { setVoice(null); toast(got.err.message); return; }
    if (!alive.current) { toast("Your voice message didn't go through. Try again."); return; }
    setVoice({ clip, failed: true });
  };
  const retryVoice = () => { if (voice?.failed && !chat.sending) void sendVoice(voice.clip); };
  const confirm = async (m: Message) => {
    setBusy(m.id);
    const before = new Set(st.plan.cards.map((c) => c.id));
    const r = await confirmPlan(m.id);
    setBusy(null);
    // Count only cards that are new on Today: plan cards that reuse a waiting card are not "added" (QA 11-01).
    const n = r ? r.state.plan.cards.filter((c) => !before.has(c.id) && c.st === 'todo').length : 0;
    if (r) toast(n ? `${n} ${n === 1 ? 'card' : 'cards'} added to Today` : 'Today is set', { action: { label: 'See today', run: () => go('T') } });
  };
  const night = async () => {
    setBusy('night');
    const r = await goodnight();
    setBusy(null);
    if (r && params.chat === 'evening') setParams({ chat: null });
  };
  const onPhoto = async (e: Event) => {
    const el = e.currentTarget as HTMLInputElement;
    const file = el.files?.[0];
    el.value = '';
    setSheet(false);
    if (!file) return;
    try {
      const img = await readPhoto(file);
      setOutgoing({ kind: 'photo', thumb: img.thumb || null }); setVoice(null); setHeard(null);
      await sendPhoto(img);
    } catch (err) { toast(errorText(err)); }
  };

  // ---------------------------------------------------------------- chips (QC1_Chat)
  type Chip = { label: string; run: () => void; strong?: boolean };
  let chips: Chip[] = [];
  let chipLabel = 'Try saying';
  const todo = st.plan.cards.filter((x) => x.st === 'todo');
  if (mode === 'first') {
    // Day 0 after onboarding: no "Good night" all day (QA 11-01); it comes with the evening wrap (SPEC "evening").
    chipLabel = '';
  } else if (mode === 'evening') {
    chips = saidGoodnight ? [] : [{ label: 'Good night', run: () => void night(), strong: true }];
    chipLabel = '';
  } else if (mode === 'ask') {
    chips = [{ label: "Here's my day", run: openTyping }, { label: 'Photo of my list', run: () => photoIn.current?.click() }];
  } else if (mode === 'n2') {
    chips = [{ label: 'Plan a short day', run: () => { setShortDay(true); openTyping(); }, strong: true }];
    chipLabel = '';
  } else if (mode === 'n3' && offer) {
    chips = [
      { label: 'Sure, 2 minutes', strong: true, run: async () => { const r = await answerOffer(offer.id, true); if (r?.cardId) go('W', { cur: r.cardId, alt: null, from: 'C' }); } },
      { label: 'Not now', run: () => void answerOffer(offer.id, false) },
    ];
    chipLabel = '';
  } else if (mode === 'planned') {
    const nowMin = localMinutes(serverNow(), st.user.tz);
    if (todo.some((x) => x.at >= 12 * 60 && x.at > nowMin)) chips.push({ label: 'Something came up this afternoon', run: () => void send('Something came up this afternoon.') });
    const big = todo.find((x) => x.min >= 20 && !x.from);
    if (big) chips.push({ label: `This one's too much: ${taskShort(big.title)}`, run: () => void send(`This one's too much: ${taskShort(big.title)}.`) });
    if (!chips.length) chips.push({ label: 'See today', run: () => go('T') });
  }
  if (chat.sending) chips = [];
  // Never suggest tasks right after a crisis (QA 11-01): the last thing in chat is the crisis card.
  const lastKind = st.messages.length ? st.messages[st.messages.length - 1].kind : null;
  const afterCrisis = lastKind === 'crisis' || (st.messages.length > 1 && st.messages[st.messages.length - 2].kind === 'crisis');
  if (afterCrisis) chips = [];
  // The next-card strip (F_Chat): while today's plan is on and a card is still to do, it takes the chips' place.
  const next = todo[0] || null;
  const strip = mode === 'planned' && st.plan.status === 'planned' && !!next && !afterCrisis;
  if (strip || (hidden && chipLabel)) chips = [];

  // ---------------------------------------------------------------- cards in chat (phone v2)
  const cardById = (id: string | null) => (id ? st.plan.cards.find((x) => x.id === id) || null : null);
  const sheetCard = cardById(cardOpen);
  const onStuck = (id: string) => { setHidden(false); setCardOpen(null); setTodayOpen(false); stuckOn(id); };
  // The live plan card: today's accepted plan, shown as the checklist of today's cards.
  const livePlan = st.plan.status === 'planned' ? lastOf(st.messages, (m) => m.kind === 'plan' && m.data?.status === 'accepted' && isToday(m, st)) : null;
  // The newest ask keeps its reasons card (the pick lit) while it is open, while her help is on its way, and while
  // that help is the open one; older asks collapse to her line.
  const sd = (m: Message | null) => (m ? (m.data as StuckChatData | undefined) : undefined);
  const lastAsk = lastOf(st.messages, (m) => m.kind === 'stuck' && sd(m)?.step === 'ask');
  const lastStuck = lastOf(st.messages, (m) => m.kind === 'stuck');
  const askD = sd(lastAsk), lastD = sd(lastStuck);
  const liveAsk = lastAsk && askD && (askD.status === 'open' || stuckWait
    || (lastStuck !== lastAsk && lastD?.step === 'help' && lastD.status === 'open' && lastD.cardId === askD.cardId)) ? lastAsk.id : null;
  const planRows = (id: string) => ((st.messages.find((x) => x.id === id)?.data as PlanData | undefined)?.cards || [])
    .map((x) => ({ time: x.time, title: x.title, min: x.min }));
  let editValue: CardValues | null = null;
  if (editing && 'card' in editing) { const x = cardById(editing.card); editValue = x ? { title: x.title, time: x.time, min: x.min } : null; }
  else if (editing) editValue = planRows(editing.plan)[editing.i] || null;
  const saveEdit = async (v: CardValues) => {
    const e = editing;
    setEditing(null);
    if (!e) return;
    if ('card' in e) { if (await editCard(e.card, v)) toast('Card changed'); return; }
    const rows = planRows(e.plan);
    if (!rows[e.i]) return;
    rows[e.i] = v;
    if (await editPlan(e.plan, rows)) toast('Card changed');
  };
  const removeRow = async () => {
    const e = editing;
    setEditing(null);
    if (!e || 'card' in e) return;
    const rows = planRows(e.plan).filter((_, j) => j !== e.i);
    if (rows.length && await editPlan(e.plan, rows)) toast('Taken off the plan');
  };

  // ---------------------------------------------------------------- render one item
  const rowsOf = (cards: ProposedCard[]) => cards.map((x, i) => <MessageRow key={i} time={x.time} title={x.title} side={`${x.min} min`} />);
  const inToday = (
    <div class={s.added}>
      <span class={s.addedText}><Icon name="check" size={16} strokeWidth={2.4} color="var(--acc)" />In Today</span>
      <button type="button" class={s.linkBtn} onClick={() => go('T')}>See today</button>
    </div>
  );
  // Evening card foot, from the wrap's own day (SummaryData.mark / streak, server README), never from litToday: a
  // finished day shows its final mark and the streak through it. While the wrap is still today's (mark 'today', or a
  // wrap the app built), today's live mark from streak.week, so a card finished after the wrap still counts. Older
  // wraps without a mark: lit or not. Only today's wrap offers to change the plan time.
  const todayMark = st.streak.week.find((d) => d.day === st.today)?.mark;
  const streakText = (n: number) => `Streak: ${n} ${n === 1 ? 'day' : 'days'}`;
  const footOf = (data: SummaryData, onToday: boolean) => {
    const final = data.mark && data.mark !== 'today' ? data.mark : null;
    const live = onToday && !final;
    const mark = final || (live ? todayMark : data.done > 0 ? 'lit' : null);
    const n = live ? st.streak.count : data.streak;
    if (mark === 'lit') return n ? streakText(n) : 'Day lit';
    if (mark === 'rest') return 'Streak safe: rest day';
    if (mark === 'freeze') return 'Streak safe: weekly freeze';
    // Today not lit yet: the weekly freeze covers it if nothing gets done (one per ISO week, streak already going).
    if (live && st.streak.count > 0 && st.streak.freezeLeft > 0) return 'Streak safe: weekly freeze';
    return null;
  };
  const evening = (key: string, line: string | null, data: SummaryData, head: string, face: boolean, onToday = true) => (
    <div key={key} class={s.stack}>
      {line && <Bubble from="buddy" ch={ch} face={face} e={data.done ? 2 : 4}>{line}</Bubble>}
      <MessageCard head={head} indent>
        {data.cards.filter((x) => x.st !== 'removed').map((x, i) => {
          const card = st.plan.cards.find((y) => y.title === x.title);
          return x.st === 'done' ? <MessageRow key={i} time={card?.time || ''} title={x.title} side="Done" sideAccent />
            : x.st === 'moved' ? <MessageRow key={i} time={card?.time || ''} title={x.title} side="Tomorrow" dim />
            : <MessageRow key={i} time={card?.time || ''} title={x.title} side="Not done" dim />;
        })}
        {(footOf(data, onToday) || onToday) && (
          <div class={s.foot}>
            {footOf(data, onToday) && <span class={s.footA}><Icon name="flame" size={18} strokeWidth={1.6} color="var(--acc)" fill="var(--acc)" />{footOf(data, onToday)}</span>}
            {onToday && (
              <span class={s.footB}>
                <span>{`Tomorrow we plan at ${planTime}`}</span>
                <button type="button" class={s.linkBtn} onClick={() => setTimeOpen(true)} aria-label={`Change the plan time, now ${planTime}`}>Change</button>
              </span>
            )}
          </div>
        )}
      </MessageCard>
    </div>
  );

  const render = (it: Item) => {
    if (it.k === 'div') return <p key={it.key} class={s.divider}>{it.text}</p>;
    if (it.k === 'stream') return <Bubble key={it.key} from="buddy" ch={ch} face={it.face} e={1}>{it.text}</Bubble>;
    if (it.k === 'typing') return <TypingBubble key={it.key} ch={ch} face={it.face} />;
    if (it.k === 'voice') return <VoiceBubble key={it.key} ms={it.v.clip.ms} levels={it.v.clip.levels} failed={it.v.failed} onRetry={retryVoice} />;
    if (it.k === 'heard') return <Bubble key={it.key} from="buddy" ch={ch} face={it.face} e={1}>{it.text}</Bubble>;
    if (it.k === 'echo') {
      const o = it.out;
      if (o.kind === 'text') return <Bubble key={it.key} from="user">{o.text}</Bubble>;
      return o.thumb
        ? <div key={it.key} class={s.userPhoto}><img src={o.thumb} alt="Your photo" class={s.userImg} /></div>
        : <div key={it.key} class={s.photo} aria-label="Photo" role="img"><span class={s.photoPaper} /></div>;
    }
    if (it.k === 'first') return (
      <MessageCard key={it.key} head="CARD 1 · DONE" indent><MessageRow time="DONE" title={it.title} /></MessageCard>
    );
    if (it.k === 'note') return (
      <div key={it.key} class={s.note}>
        <Icon name="flame" size={28} strokeWidth={1.6} color="var(--acc)" fill="var(--acc)" />
        <span class={s.noteText}><span class={s.noteHead}>{it.head}</span><span class={s.noteBody}>{it.text}</span></span>
      </div>
    );
    if (it.k === 'eve') return evening(it.key, it.line, it.data, it.head, true);
    const m = it.m;
    if (m.role === 'user') {
      if (m.image) return (
        <div key={it.key} class={s.userPhoto}>
          {m.image.startsWith('data:') || m.image.startsWith('/') || m.image.startsWith('http')
            ? <img src={m.image} alt="Your photo" class={s.userImg} />
            : <div class={s.photo} role="img" aria-label="Your photo"><span class={s.photoPaper} /></div>}
          {m.text && <Bubble from="user">{m.text}</Bubble>}
        </div>
      );
      return <Bubble from="user" voice={!!m.voice}>{m.text}</Bubble>;
    }
    switch (m.kind) {
      case 'plan': {
        const d = m.data as PlanData;
        const pending = d?.status === 'pending';
        const head = `TODAY · ${dateHead(dayOf(m.ts, st.user.tz))}`;
        // Before Looks good: tap a row to fix it (F_Edit, editPlan).
        if (pending && isToday(m, st)) return (
          <MessageCard key={it.key} head={head} indent>
            <p class={s.fixHint}>Heard something wrong? Tap a card to fix it.</p>
            {(d.cards || []).map((x, i) => (
              <button key={i} type="button" class={s.planRow} aria-label={`Change this card: ${x.title}`} onClick={() => setEditing({ plan: m.id, i })}>
                <span class={s.planTime}>{x.time}</span>
                <span class={s.planTitle}>{x.title}</span>
                <span class={s.planSide}>{`${x.min} min`}</span>
                <Icon name="pencil" size={15} color="#CFC8BC" />
              </button>
            ))}
            <div class={s.planBtns}>
              <PrimaryButton h={46} onClick={() => void confirm(m)} busy={busy === m.id} data-cta="looks-good" style={{ flex: '1 1 auto', padding: '0 8px' }}>Looks good</PrimaryButton>
              <button type="button" class={s.change} onClick={openTyping}>Change something</button>
            </div>
          </MessageCard>
        );
        // After Looks good: today's cards as a checklist (tick = the light celebration; a row = the card sheet).
        if (livePlan && m.id === livePlan.id) {
          const cards = st.plan.cards.filter((x) => x.st !== 'removed' && x.st !== 'moved');
          const doneN = cards.filter((x) => x.st === 'done').length;
          return (
            <MessageCard key={it.key} indent>
              <div class={s.planHead}>
                <p class={s.planHeadText}>{narrow ? `TODAY · ${dateHeadShort(dayOf(m.ts, st.user.tz))}` : head}</p>
                <span class={s.planCount}>{`${doneN} of ${cards.length} done`}</span>
              </div>
              {cards.map((x) => {
                const done = x.st === 'done';
                return (
                  <div key={x.id} class={s.checkRow} data-done={done ? 'true' : 'false'} data-card-row={x.id}>
                    <TickButton done={done} title={x.title} w={36} h={44} style={{ marginLeft: -8 }} onTick={() => { pinRow(x.id); void tick(x, 'C'); }} />
                    <button type="button" class={s.planRow} data-check="true" aria-label={`Card: ${x.title}`} aria-disabled={done ? 'true' : undefined} onClick={() => { if (!done) setCardOpen(x.id); }}>
                      <span class={s.planTime}>{x.time}</span>
                      <span class={s.planTitle}>{x.title}</span>
                      <span class={s.planSide}>{done ? 'Done' : `${x.min} min`}</span>
                    </button>
                  </div>
                );
              })}
              <div class={s.added}>
                <span class={s.addedText}>Tick a card when it's done</span>
                <button type="button" class={s.linkBtn} onClick={() => go('T')}>See today</button>
              </div>
            </MessageCard>
          );
        }
        return (
          <MessageCard key={it.key} head={head} indent class={d?.status === 'replaced' ? s.dim : undefined}>
            {rowsOf(d?.cards || [])}
            {pending && (
              <div class={s.planBtns}>
                <PrimaryButton h={46} onClick={() => void confirm(m)} busy={busy === m.id} data-cta="looks-good" style={{ flex: '1 1 auto', padding: '0 8px' }}>Looks good</PrimaryButton>
                <button type="button" class={s.change} onClick={openTyping}>Change something</button>
              </div>
            )}
            {d?.status === 'accepted' && isToday(m, st) && inToday}
          </MessageCard>
        );
      }
      case 'change': {
        const d = m.data as ChangeData;
        const undone = !!d?.undone;
        const onlySplit = (d?.items || []).every((x) => x.type === 'split');
        return (
          <MessageCard key={it.key} indent class={undone ? s.dim : undefined}>
            {(d?.items || []).map((x, i, all) => {
              const cards = x.cardIds.map((id) => st.plan.cards.find((y) => y.id === id) || st.tomorrow.find((y) => y.id === id)).filter(Boolean) as AppState['plan']['cards'];
              const first = cards[0];
              const headOf = (y: typeof x) => (y.type === 'moved' ? 'MOVED TO TOMORROW' : y.type === 'split' ? `SPLIT INTO ${y.cardIds.length} SMALL CARDS`
                : y.type === 'removed' ? (y.detail === 'Not in the plan' ? 'NOT IN THE PLAN · REMOVED' : 'REMOVED') : y.type === 'added' ? 'ADDED' : `MOVED TO ${y.detail}`);
              const head = headOf(x);
              const showHead = i === 0 || headOf(all[i - 1]) !== head;
              return (
                <div key={i} class={s.changeItem} data-head={showHead ? 'true' : 'false'}>
                  {showHead && <p class={s.changeHead}>{head}</p>}
                  {x.type === 'split'
                    ? cards.map((y) => <MessageRow key={y.id} time={y.time} title={y.title} side={`${y.min} min`} />)
                    : x.type === 'moved' || x.type === 'removed'
                      ? <MessageRow time={first?.time || ''} title={x.title} strike side={x.type === 'moved' ? 'Tomorrow' : ''} sideAccent />
                      : <MessageRow time={first?.time || x.detail} title={x.title} side={first ? `${first.min} min` : ''} />}
                </div>
              );
            })}
            {!undone
              ? <button type="button" class={s.undo} onClick={() => void undoChange(d.changeId)}>{onlySplit ? 'Keep it as one' : 'Undo'}</button>
              : <p class={s.undone}>Undone</p>}
          </MessageCard>
        );
      }
      case 'summary': {
        const d = m.data as SummaryData;
        return evening(it.key, m.text, d, dateHead(dayOf(m.ts, st.user.tz)), it.face, isToday(m, st));
      }
      case 'tomorrow': {
        const d = m.data as { card: ProposedCard; day?: string };
        return (
          <MessageCard key={it.key} head={d?.day === st.today && st.dayIndex === 0 ? 'TONIGHT' : "FIRST ON TOMORROW'S LIST"} indent>
            <MessageRow time={d?.card?.time || ''} title={d?.card?.title || ''} />
          </MessageCard>
        );
      }
      case 'nudge': {
        const d = m.data as { card: ProposedCard; status: string };
        return (
          <div key={it.key} class={s.stack}>
            <Bubble from="buddy" ch={ch} face={it.face} e={4}>{m.text}</Bubble>
            <MessageCard indent>
              <MessageRow time={d?.card?.time || ''} title={d?.card?.title || ''} side={d?.card ? `${d.card.min} min` : ''} />
              {d?.status === 'pending'
                ? <div class={s.planBtns}><PrimaryButton h={46} grow={1} busy={busy === m.id} onClick={async () => { setBusy(m.id); await acceptNudge(m.id); setBusy(null); }}>Accept</PrimaryButton></div>
                : d?.status === 'accepted' ? inToday : <span class={s.addedText}>Expired</span>}
            </MessageCard>
          </div>
        );
      }
      case 'stuck':
        return <StuckMsg key={it.key} m={m} ch={ch} face={it.face} current={m.id === liveAsk} onWait={setStuckWait} />;
      case 'done':
        return <DoneNote key={it.key} m={m} ch={ch} />;
      case 'crisis':
        return (
          <button key={it.key} type="button" class={s.crisis} onClick={() => go('M6')}>
            <Icon name="shield" size={24} color="var(--acc)" />
            <span>{CRISIS.title}</span>
            <Icon name="chevron" size={18} color="#CFC8BC" />
          </button>
        );
      case 'limit':
        return (
          <MessageCard key={it.key} variant="note" indent>
            <p class={s.cardText}>{m.text}</p>
            <div class={s.planBtns}><PrimaryButton h={46} grow={1} onClick={() => go('M4')}>See VIP</PrimaryButton></div>
          </MessageCard>
        );
      case 'week':
        return (
          <div key={it.key} class={s.stack}>
            <Bubble from="buddy" ch={ch} face={it.face} e={4}>{m.text}</Bubble>
            <MessageCard indent head={st.week?.label ? `YOUR WEEK · ${st.week.label}` : 'YOUR WEEK'}>
              <div class={s.added} style={{ borderTop: 0, marginTop: 0, paddingTop: 0 }}>
                <span class={s.addedText}><Icon name="gift" size={16} color="var(--acc)" />{st.week ? `${st.week.cards} ${st.week.cards === 1 ? 'card' : 'cards'} · ${st.week.lit} ${st.week.lit === 1 ? 'day' : 'days'} lit` : ''}</span>
                <button type="button" class={s.linkBtn} onClick={() => go('U3')}>See your week</button>
              </div>
            </MessageCard>
          </div>
        );
      case 'unlock': {
        const stop = m.data as RouteStop;
        const kind = ({ keep: 'NEW KEEPSAKE', chap: 'NEW CHAPTER', place: 'NEW LOOK', sound: 'NEW SOUND', season: 'COMING NEXT' } as Record<string, string>)[stop?.type] || 'UNLOCKED';
        return (
          <MessageCard key={it.key} variant="note" indent head={kind}>
            <div class={s.added} style={{ borderTop: 0, marginTop: 0, paddingTop: 0 }}>
              <span class={s.cardText}>{m.text}</span>
              <button type="button" class={s.linkBtn} onClick={() => (stop?.type === 'chap' && stop.chap ? go('U2', { chap: stop.chap }) : go('U1'))}>{stop?.type === 'chap' ? 'Read it' : 'See it'}</button>
            </div>
          </MessageCard>
        );
      }
      case 'offer':
      case 'planned':
      case 'text':
      default:
        return m.text ? <Bubble key={it.key} from="buddy" ch={ch} face={it.face} e={m.kind === 'planned' ? 4 : 1}>{m.text}</Bubble> : null;
    }
  };

  const n = items.length;
  const narrow = typeof window !== 'undefined' && window.innerWidth < 380;
  // Short phones (360x640): a shorter fade at the top of the messages, so a card in the log stays readable; narrow
  // ones: "TUE, NOV 10" in the checklist header, so it stays on one line beside the count.
  const shortScreen = typeof window !== 'undefined' && window.innerHeight < 720;

  return (
    <Screen theme={ch} id="C" tab="chat" badge={st.plan.status === 'planned' ? todoLeft : 0} class={desk ? undefined : s.glass}>
      <div class={desk ? s.sceneDesk : s.scene} aria-hidden="true">
        <XScene ch={ch} stage={st.buddy.stage} mode="clear" pos="50% 30%" alt="" />
        <div class={desk ? s.sceneFade : s.veil} data-hidden={hidden ? 'true' : 'false'} />
      </div>

      <header class={s.head}>
        <button type="button" class={s.buddy} onClick={() => go('M2')} aria-label={`${c.name}'s page`}>
          <XFace ch={ch} e={1} s={36} ring="var(--acc)" alt="" />
          <span class={s.buddyText}>
            <span class={s.buddyName}>{c.name}<span class={s.ai}>AI</span></span>
            <span class={s.buddyStatus}>{c.placeAt}</span>
          </span>
        </button>
        <StreakPill count={st.streak.count} lit={st.streak.litToday} onClick={() => go('U1')} />
      </header>

      {/* Tap her (the space above the messages) to hide the chat; "Show the chat" brings it back (F_Chat). */}
      {!desk && (
        <div class={s.peek} data-hidden={hidden ? 'true' : 'false'}>
          <button
            type="button" class={s.peekBtn} onClick={() => setHidden(!hidden)}
            aria-label={`Hide the chat to see ${c.name}`} aria-hidden={hidden ? 'true' : undefined} tabIndex={hidden ? -1 : undefined}
          />
          {hidden && (
            <div class={s.showWrap}>
              <button type="button" class={s.showChat} onClick={() => setHidden(false)}>
                <Icon name="chat" size={18} color="#F5F1EA" /><span>Show the chat</span>
              </button>
            </div>
          )}
        </div>
      )}

      {!hidden && (
        <ScrollArea reverse class={desk ? s.logDesk : s.log} fadeTop={desk ? 30 : shortScreen ? 24 : 64} fadeBottom={0} role="log" aria-label={`Chat with ${c.name}`} aria-live="polite">
          <div ref={msgsRef} class={desk ? s.msgsDesk : s.msgs}>
            {items.map((it, i) => (
              <div key={it.key} class={s.item} data-k={it.k} style={i >= n - 3 ? { animation: 'xmRise 380ms ease-out both' } : undefined}>{render(it)}</div>
            ))}
          </div>
        </ScrollArea>
      )}
      {/* Room for the toast above the strip, so it never covers the newest message. */}
      {!hidden && !desk && toastOn && atBottom && <div class={s.toastRoom} aria-hidden="true" />}

      {strip && next ? (
        <div class={s.strip} role="group" aria-label="Next card">
          <button type="button" class={s.stripTick} aria-label={`Mark done: ${next.title}`} onClick={() => void tick(next, 'C')}>
            <span class={s.stripRing} aria-hidden="true" />
          </button>
          <button type="button" class={s.stripMid} aria-label={`All of today's cards. Up next: ${next.title}`} onClick={() => setTodayOpen(true)}>
            <span class={s.stripLabel}>{`UP NEXT · ${next.time}`}</span>
            <span class={s.stripTitle}>{next.title}</span>
          </button>
          <button type="button" class={s.stripStuck} aria-label={`Stuck: ${next.title}`} onClick={() => onStuck(next.id)}>
            <Icon name="lifebuoy" size={20} color="#F5F1EA" />
          </button>
          <button type="button" class={s.stripStart} aria-label={`Start: ${next.title}`} onClick={() => work(next.id, null, 'C')}>
            <Icon name="play" size={14} color="var(--on)" fill="var(--on)" /><span>Start</span>
          </button>
        </div>
      ) : (
        <div class={s.chips} role="group" aria-label="Suggestions">
          {chips.length > 0 && chipLabel && <span class={s.chipLabel}>{chipLabel}</span>}
          {chips.map((q) => (
            <button key={q.label} type="button" class={s.chip} data-strong={q.strong ? 'true' : 'false'} onClick={q.run} disabled={busy === 'night'}>{q.label}</button>
          ))}
        </div>
      )}

      <div class={s.bar}>
        {desk ? (
          <DeskTalkBar
            name={c.name} value={draft} onInput={setDraft} inputRef={input}
            onSend={() => void send(draft)}
            onAudio={(clip) => { void sendVoice(clip); }}
            onAttach={() => setSheet(true)}
            onError={(e) => { micError(e, 'You can type instead.'); input.current?.focus(); }}
            onTooShort={() => toast('Too short. Nothing was sent.')}
            onCancelled={() => toast('Cancelled. Nothing was sent.')}
            disabled={chat.sending}
          />
        ) : typing ? (
          <form class={s.typeBar} onSubmit={(e) => { e.preventDefault(); void send(draft); }}>
            <IconButton icon="close" label="Close keyboard" variant="ghost" size={44} iconSize={20} iconColor="#CFC8BC" onClick={() => setTyping(false)} />
            <input
              ref={input} type="text" class={s.input} aria-label={`Message ${c.name}`} placeholder={`Message ${c.name}`} value={draft}
              enterKeyHint="send" autoComplete="off" onInput={(e) => setDraft((e.currentTarget as HTMLInputElement).value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setTyping(false); }}
            />
            <button type="submit" class={s.send} aria-label="Send" disabled={chat.sending}>
              <Icon name="send" size={20} strokeWidth={2.4} color="var(--on)" />
            </button>
          </form>
        ) : (
          <HoldToTalk
            ariaLabel={`Tap to type, or hold to talk to ${c.name}`}
            onTap={openTyping}
            onAudio={(clip) => { void sendVoice(clip); }}
            onError={(e) => { micError(e, 'You can type instead.'); openTyping(); }}
            onTooShort={() => toast('Hold a little longer to talk, or tap to type.')}
            disabled={chat.sending}
            left={<IconButton icon="plus" label="Add a photo or file" size={44} iconSize={22} variant="ghost" style={{ background: 'rgba(255,255,255,0.08)' }} disabled={chat.sending} onClick={() => setSheet(true)} />}
            right={<IconButton icon="camera" label="Take a photo of your list" size={44} iconSize={22} variant="ghost" style={{ background: 'rgba(255,255,255,0.08)' }} disabled={chat.sending} onClick={() => cameraIn.current?.click()} />}
          />
        )}
      </div>

      <input ref={photoIn} type="file" accept="image/*" class="xm-sr-only" tabIndex={-1} aria-hidden="true" onChange={(e) => void onPhoto(e)} />
      <input ref={cameraIn} type="file" accept="image/*" capture="environment" class="xm-sr-only" tabIndex={-1} aria-hidden="true" onChange={(e) => void onPhoto(e)} />

      {sheet && (
        <>
          <button type="button" class={s.scrim} aria-label="Close" onClick={() => setSheet(false)} />
          <div class={s.attach} role="dialog" aria-label="Add to the chat">
            <div class={s.attachRow}>
              <button type="button" class={s.attachBtn} onClick={() => photoIn.current?.click()}><Icon name="photo" size={26} strokeWidth={1.9} color="var(--acc)" />Photo</button>
              <button type="button" class={s.attachBtn} onClick={() => cameraIn.current?.click()}><Icon name="camera" size={26} strokeWidth={1.9} color="var(--acc)" />Camera</button>
            </div>
            <p class={s.attachNote}>{`Send a photo of your to-do list and ${c.name} will turn it into cards.`}</p>
          </div>
        </>
      )}

      <TimeSheet open={timeOpen} value={planTime} onClose={() => setTimeOpen(false)} />
      <CardSheet card={sheetCard && sheetCard.st === 'todo' ? sheetCard : null} from="C" onClose={() => setCardOpen(null)}
        onEdit={(x) => setEditing({ card: x.id })} onLater={(x) => setLaterOf(x)} />
      <LaterSheet card={laterOf} onClose={() => setLaterOf(null)} />
      <TodaySheet open={todayOpen} onClose={() => setTodayOpen(false)} onCard={(x) => setCardOpen(x.id)} />
      <EditSheet
        open={!!editing} value={editValue} plan={!!editing && 'plan' in editing} onClose={() => setEditing(null)} onSave={(v) => void saveEdit(v)}
        onRemove={editing && 'plan' in editing && planRows(editing.plan).length > 1 ? () => void removeRow() : undefined}
      />
    </Screen>
  );
}

/** "When should we plan?" with the time wheel (third round: XTime), from the evening card's "Change". */
function TimeSheet({ open, value, onClose }: { open: boolean; value: string; onClose: () => void }) {
  const [v, setV] = useState(value);
  useEffect(() => { if (open) setV(value); }, [open]);
  return (
    <BottomSheet open={open} onClose={onClose} label="When should we plan?">
      <p style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>When should we plan?</p>
      <XTime value={v} onPick={setV} />
      <PrimaryButton h={54} onClick={() => { onClose(); if (v !== value) void updateSettings({ planTime: v }); }}>Done</PrimaryButton>
    </BottomSheet>
  );
}

// ------------------------------------------------------------------ messages -> items
/** Messages -> chat items and the chat mode (shared with the desktop room, screens/desktop/Room.tsx, which renders them as subtitles). `waiting`: her stuck help is on its way
 *  (phone v2), so she types at the end. Undone 'done' notes are left out. */
export function derive(st: AppState, chat: { sending: boolean; pending: Message[]; text: string }, outgoing: Outgoing | null, voice: VoiceOut | null, heard: string | null, askedEvening: boolean, shortDay: boolean, waiting = false) {
  const tz = st.user.tz;
  const ids = new Set(st.messages.map((m) => m.id));
  const msgs = st.messages.concat(chat.pending.filter((m) => !ids.has(m.id)));
  const today = st.today;
  const yesterday = addDays(today, -1);
  const todays = msgs.filter((m) => dayOf(m.ts, tz) === today);
  const saidGoodnight = todays.some((m) => m.role === 'user' && m.text === 'Good night.');
  const serverEvening = todays.some((m) => m.kind === 'summary');
  const offer = lastOf(todays, (m) => m.kind === 'offer' && m.data?.status === 'pending');
  const pendingPlan = lastOf(todays, (m) => m.kind === 'plan' && m.data?.status === 'pending');

  let mode: Mode;
  if ((st.hints.evening || askedEvening) && !saidGoodnight) mode = 'evening';
  else if (st.dayIndex === 0) mode = 'first';
  else if (offer) mode = 'n3';
  else if (pendingPlan || st.plan.status === 'proposed') mode = 'proposed';
  else if (st.hints.back && st.plan.status !== 'planned' && !shortDay) mode = 'n2';
  else if (st.plan.status === 'none' || st.plan.status === 'asked') mode = 'ask';
  else mode = 'planned';

  const items: Item[] = [];
  let prevDay = '', prevMs = 0;
  let buddyRun = false; // a buddy bubble right after a buddy item hides its face
  const firstCard = st.dayIndex === 0 ? st.plan.cards.find((c) => c.source === 'onboarding' && c.st === 'done') : undefined;
  let firstPlaced = !firstCard;
  let sawUser = false;
  const backLine = LINES[st.ch].back;
  const lastBack = lastOf(todays, (m) => m.role === 'buddy' && m.text === backLine);

  for (const m of msgs) {
    if (m.kind === 'done' && m.data?.undone) continue;
    const ms = Date.parse(m.ts);
    const d = dayOf(m.ts, tz);
    if (d !== prevDay) {
      items.push({ k: 'div', key: 'd' + m.id, text: `${d === today ? 'Today' : d === yesterday ? 'Yesterday' : dateLabel(d)}, ${timeOf(m.ts, tz)}` });
      buddyRun = false;
    } else if (ms - prevMs > 90 * MIN) {
      items.push({ k: 'div', key: 'd' + m.id, text: timeOf(m.ts, tz) });
      buddyRun = false;
    }
    prevDay = d; prevMs = ms;
    // Day 0: "CARD 1 · DONE" after the buddy's first reply (QC1_Chat mode first).
    if (!firstPlaced && d === today && (m.kind === 'tomorrow' || (sawUser && m.role === 'buddy' && items.length && (items[items.length - 1] as { m?: Message }).m?.role === 'buddy'))) {
      items.push({ k: 'first', key: 'first', title: firstCard!.title });
      firstPlaced = true; buddyRun = true;
    }
    if (m.role === 'user') sawUser = true;
    const isBuddyBubble = m.role === 'buddy' && ['text', 'planned', 'offer', 'summary', 'nudge', 'week', 'stuck'].includes(m.kind);
    items.push({ k: 'msg', key: m.id, m, face: isBuddyBubble ? !buddyRun : false });
    buddyRun = m.role !== 'user';
    if (lastBack && m.id === lastBack.id && st.hints.back && st.streak.safeNote) {
      items.push({ k: 'note', key: 'safe', head: 'Your streak is safe', text: st.streak.safeNote });
    }
  }
  if (!firstPlaced && firstCard) items.push({ k: 'first', key: 'first', title: firstCard.title });
  if (askedEvening && !serverEvening && !saidGoodnight) {
    const l = localSummary(st);
    items.push({ k: 'div', key: 'eve-d', text: timeOf(new Date(serverNow()).toISOString(), tz) });
    items.push({ k: 'eve', key: 'eve', line: l.line, data: l.data, head: dateHead(today) });
    buddyRun = true;
  }
  // Live stream: local echo until the server's echo arrives, then the streamed text (or dots).
  const pendingUser = chat.pending.some((m) => m.role === 'user');
  if (chat.sending && outgoing !== null && !pendingUser) { items.push({ k: 'echo', key: 'echo', out: outgoing }); buddyRun = false; }
  // A voice message on its way (no dots until its words are in), or one that didn't go through.
  const voiceWaits = !!voice && !pendingUser;
  if (voiceWaits) { items.push({ k: 'voice', key: 'voice', v: voice! }); buddyRun = false; }
  if (heard && !chat.sending) { items.push({ k: 'heard', key: 'heard', text: heard, face: !buddyRun }); buddyRun = true; }
  if (chat.text) items.push({ k: 'stream', key: 'stream', text: chat.text, face: !buddyRun });
  else if ((chat.sending && !voiceWaits && !chat.pending.some((m) => m.role !== 'user')) || (waiting && !chat.sending)) items.push({ k: 'typing', key: 'typing', face: !buddyRun });
  return { items, mode, offer, saidGoodnight };
}

